import { useState } from 'react'
import {
  Bell,
  Shield,
  Moon,
  Globe,
  Key,
  Save
} from 'lucide-react'

export function SettingsPage() {
  const [settings, setSettings] = useState({
    notifications: true,
    autoScan: true,
    scanFrequency: 'weekly',
    darkMode: true,
    language: 'en',
  })

  const handleToggle = (key: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleChange = (key: keyof typeof settings, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
        <p className="text-slate-400">
          Customize your PC Health Assistant experience
        </p>
      </div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {/* Notifications */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Notifications</h3>
                <p className="text-sm text-slate-400">Receive alerts about system issues</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('notifications')}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.notifications ? 'bg-brand-primary' : 'bg-slate-600'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                settings.notifications ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
        </div>

        {/* Auto Scan */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Automatic Scans</h3>
                <p className="text-sm text-slate-400">Run scheduled diagnostic scans</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('autoScan')}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.autoScan ? 'bg-brand-primary' : 'bg-slate-600'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                settings.autoScan ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          
          {settings.autoScan && (
            <div className="ml-14">
              <label className="text-sm text-slate-400 block mb-2">Scan Frequency</label>
              <select
                value={settings.scanFrequency}
                onChange={(e) => handleChange('scanFrequency', e.target.value)}
                className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          )}
        </div>

        {/* Appearance */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Moon className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Dark Mode</h3>
                <p className="text-sm text-slate-400">Use dark theme (recommended)</p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('darkMode')}
              className={`w-12 h-6 rounded-full transition-colors ${
                settings.darkMode ? 'bg-brand-primary' : 'bg-slate-600'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                settings.darkMode ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
        </div>

        {/* Language */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Language</h3>
                <p className="text-sm text-slate-400">Choose your preferred language</p>
              </div>
            </div>
            <select
              value={settings.language}
              onChange={(e) => handleChange('language', e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>

        {/* API Key (for premium features) */}
        <div className="card">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">API Configuration</h3>
              <p className="text-sm text-slate-400">Configure your Claude API key for AI features</p>
            </div>
          </div>
          <div className="ml-14">
            <input
              type="password"
              placeholder="sk-ant-..."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
            <p className="text-xs text-slate-500 mt-2">
              Your API key is stored locally and never sent to our servers.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8">
        <button className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save Settings
        </button>
      </div>
    </div>
  )
}
